import { useState, useEffect, useCallback } from 'react'
import { usePerson } from '@fs/zion-person-service'
import {
  clearLastViewedPid,
  getLastViewedPid,
  getLastViewedPedigree,
  getTreeStartingPid,
  setLastViewedPedigree,
  setLastViewedPid,
  type useGroupsContext,
} from '@fs/zion-tree-groups'
import { type PID } from '@fs/zion-tree-types'
import { type PedigreeViewType } from '../../PedigreeRoutes'

type GetPedigreeAndPidArgs = {
  treeId?: string
}

type GetPedigreeAndPidReturn = {
  view: PedigreeViewType
  pid: PID
  startingPid?: PID
}

export const getPedigreeAndPid = async ({ treeId }: GetPedigreeAndPidArgs): Promise<GetPedigreeAndPidReturn> => {
  const lastViewedPedigree = await getLastViewedPedigree()
  const lastViewedPidAndTree = await getLastViewedPid('pedigree')
  // @ts-expect-error -- update later so we keep history for file
  const [lastPid, lastPidTreeId] = lastViewedPidAndTree?.split(':') || []
  const lastViewedPid = treeId === lastPidTreeId ? lastPid : null
  const view = lastViewedPedigree || 'portrait'

  const { personId: startingPid } = (await getTreeStartingPid(treeId || 'PRIVATE')) || {}
  const pid = lastViewedPid || startingPid || ''
  // @ts-expect-error -- update later so we keep history for file
  return { view, pid, startingPid }
}

type UsePedigreeDataArgs = {
  treeId?: ReturnType<typeof useGroupsContext>['currentTreeId']
  groupsLoading: ReturnType<typeof useGroupsContext>['loading']
}

type UsePedigreeDataReturn = {
  lastView?: PedigreeViewType
  lastPid?: PID
  startingPid?: PID
  /** True when a starting person was stored but has since been deleted (distinct from never having one set). */
  startingPersonDeleted?: boolean
  loading: boolean
  updateLastView: (view: PedigreeViewType) => void
  updateLastPid: (pid: PID) => void
}
export const usePedigreeData = ({ treeId, groupsLoading }: UsePedigreeDataArgs): UsePedigreeDataReturn => {
  const [startingData, setStartingData] = useState<Omit<UsePedigreeDataReturn, 'updateLastView' | 'updateLastPid'>>({
    loading: true,
  })
  // Once a starting person is confirmed deleted we remember its pid so we stop re-verifying it —
  // usePerson runs with ignoreCache, so without this it would refetch the dead pid every render.
  const [confirmedDeletedPid, setConfirmedDeletedPid] = useState<PID>()

  const startingPidConfirmedDeleted =
    startingData.startingPid !== undefined && startingData.startingPid === confirmedDeletedPid

  // Skip the network round-trip once we already know the pid is deleted.
  const startingPidToVerify = startingPidConfirmedDeleted ? undefined : startingData.startingPid
  const startingPerson = usePerson(startingPidToVerify, 'summary', undefined, false, true)

  const updateLastView = useCallback((view) => {
    setLastViewedPedigree(view)
    setStartingData((prev) => ({ ...prev, lastView: view }))
  }, [])

  const updateLastPid = useCallback(
    (pid) => {
      setLastViewedPid(`${pid}:${treeId}`, 'pedigree')
      setStartingData((prev) => ({ ...prev, lastPid: pid }))
    },
    [treeId]
  )

  useEffect(() => {
    let mounted = true

    if (!groupsLoading) {
      // Get fallback values for pid and view if not contained in the URL
      getPedigreeAndPid({ treeId })
        .then(({ view, pid, startingPid }) => {
          return mounted && setStartingData({ lastView: view, lastPid: pid, startingPid, loading: false })
        })
        .catch(() => undefined)
    }

    return () => {
      mounted = false
    }
  }, [groupsLoading, treeId])

  const verifiedData: UsePedigreeDataReturn = { ...startingData, updateLastView, updateLastPid }

  if (startingData.startingPid) {
    const startingPersonMissing =
      startingPerson !== undefined && (startingPerson.id === 'UNKNOWN' || startingPerson.deleted)

    if (startingPidConfirmedDeleted || startingPersonMissing) {
      // A starting person was stored but no longer exists. Null the pid so we don't render the dead
      // person, and flag it as deleted so the route shows the "choose a new starting person" dialog
      // rather than treating it like a brand-new tree with none set. Clear lastPid unconditionally:
      // even a valid, *different* lastPid would otherwise become the redirect target and ping-pong
      // with PedigreeView's deleted-person redirect. Emptying it settles the flow at /pedigree/<view>/
      // with the dialog open — the same path a tree with no starting person follows.
      verifiedData.startingPid = undefined
      verifiedData.startingPersonDeleted = true
      verifiedData.lastPid = '' as unknown as PID
    } else if (startingPerson === undefined) {
      // Still verifying — block until usePerson resolves.
      verifiedData.loading = true
    }
  }

  const { startingPersonDeleted } = verifiedData
  useEffect(() => {
    // Remember the dead pid so we stop re-verifying it, and drop the persisted last-viewed pid —
    // otherwise it gets resurrected as the redirect target on the next load (keeping the user stuck).
    if (startingPersonDeleted && startingData.startingPid && confirmedDeletedPid !== startingData.startingPid) {
      setConfirmedDeletedPid(startingData.startingPid)
      clearLastViewedPid('pedigree')
    }
  }, [startingPersonDeleted, startingData.startingPid, confirmedDeletedPid])

  return verifiedData
}
